import { useEffect, useId, useState } from "react";
import { Activity, ArrowUpCircle, ArrowUpRight, Clock3, Monitor, Search, Smartphone, Wifi, WifiOff, Zap } from "lucide-react";
import { Badge, Button, Dialog, DialogActions, Field, Select, Table } from "@studio";
import { api, errorMessage } from "../lib/api";
import { can } from "../lib/access";
import { useAuth } from "../lib/auth";
import { ago, deviceState, fmtDateTime, fmtTime, machineStatus, num, taskLabel, taskTypeNames } from "../lib/format";
import { native, type LocalStatus } from "../lib/native";
import { useQuery } from "@tanstack/react-query";
import { keys, useAction, useDevices, useMachine, useReleaseMachines, useTasks } from "../lib/queries";
import type { CustomerStatus, Device, MachineDetail, MachineRow, Pairing, TaskRow } from "../lib/types";
import { ActiveRunCards, cardFromTask } from "./active-runs";
import { CopyButton, Empty, ErrorBox, InfoRow, Loading, Panel, StatGrid, StatusBadge, Tabs } from "../console/ui";
import { MachineLiveState } from "./monitoring";
import { MachineVersionPanel } from "./release";
import { MachineAiMode } from "@studio-features/meta-crew/llm-usage";
import { MachineDeviceSummary } from "@studio-features/meta-crew/machine-device-summary";
import { readiness as relayReadiness, useAiModels } from "../lib/llm-api";
import { MetricGrid, urlParam } from "../console/metrics";

const installerUrl = `${window.location.origin}/download/Meta-Crew-Worker-Setup.exe`;

/** 머신별 "최신 배포본보다 낮음" (직원만: 릴리즈 조회 권한). machineId → 최신 버전. */
function useUpdatable(customerId = "", enabled = true): Map<string, string> {
  const me = useAuth((s) => s.me);
  const rel = useReleaseMachines(customerId, enabled && can(me, "SYSTEM_READ"));
  const latest = rel.data?.latest?.version;
  const out = new Map<string, string>();
  if (latest) for (const r of rel.data?.machines ?? []) if (r.updateAvailable) out.set(r.machineId, latest);
  return out;
}

/** 시안 MachineMonitoring 의 상태 필터 (요청서 21절): 전체 / 연결 끊김 / 업데이트 가능 / 삭제됨(관리자). */
type MachineFilter = "all" | "online" | "disconnected" | "update" | "deleted";
const MACHINE_FILTERS: readonly MachineFilter[] = ["all", "online", "disconnected", "update", "deleted"];

/**
 * 머신 목록 (모니터링 › 머신, 고객사 머신 목록). 시안 4893fae MachineMonitoring 을 실제 데이터로 다시 조립했다:
 * 시안 부품은 삭제된 머신 카드에 상세 버튼이 없고 onSelect 가 (고객사, 이름) 만 넘겨, 삭제한 머신의 작업 기록 보기(2026-10-21 사용자 요구)와
 * 같은 이름 머신 구분이 안 된다. 그래서 같은 클래스로 그리고 머신 ID 로 연다 (요청서 21절에 시안 보완 요청).
 * showCustomer = 관리 콘솔(전체 고객사) 범위. 고객 회원은 폰·승인 대기만 보인다 (실행·AI 지표 숨김).
 */
export function FilteredMachineCards({
  rows,
  onOpen,
  showCustomer = false,
  deletedUrl,
}: {
  rows: MachineRow[];
  onOpen: (m: MachineRow) => void;
  showCustomer?: boolean;
  /** 삭제한 머신 목록 주소 (관리자만 넘긴다). 없으면 "삭제됨" 필터를 숨긴다. */
  deletedUrl?: string;
}) {
  const me = useAuth((s) => s.me);
  const staff = !!me?.isStaff;
  // KPI 카드에서 오면 ?filter= 로 첫 필터 (2026-10-21). 삭제됨은 삭제 목록을 볼 수 있을 때만.
  const [filter, setFilter] = useState<MachineFilter>(() => {
    const v = urlParam("filter") as MachineFilter | null;
    return v && MACHINE_FILTERS.includes(v) && (v !== "deleted" || !!deletedUrl) ? v : "all";
  });
  const [query, setQuery] = useState("");
  const [customerId, setCustomerId] = useState("");
  const updatable = useUpdatable("", rows.length > 0);
  const deletedQ = useQuery({ queryKey: ["machines-deleted", deletedUrl], queryFn: () => api.page<MachineRow>(deletedUrl!), enabled: !!deletedUrl });
  const deletedRows = deletedQ.data?.items ?? [];
  const customers = [...new Map([...rows, ...deletedRows].map((m) => [m.customerId, m.customerName ?? m.customerId])).entries()].sort((x, y) => x[1].localeCompare(y[1]));
  const q = query.trim().toLowerCase();
  const source = filter === "deleted" ? deletedRows : rows;
  const visible = source.filter(
    (m) =>
      (filter !== "disconnected" || !m.connected) &&
      (filter !== "online" || m.connected) &&
      (filter !== "update" || updatable.has(m.id)) &&
      (!customerId || m.customerId === customerId) &&
      (!q || [m.label, m.customerName, m.version].some((v) => v?.toLowerCase().includes(q))),
  );
  return (
    <div className="meta-crew-machine-monitoring">
      {showCustomer && (
        <section className="meta-crew-machine-overview" aria-label="전체 고객사 머신 요약">
          <div className="meta-crew-machine-overview-heading">
            <strong>전체 고객사 머신</strong>
            <span>삭제된 머신 제외</span>
          </div>
          {/* 카드를 누르면 아래 목록을 그 상태로 거른다 (2026-10-21) */}
          <MetricGrid
            columns={3}
            items={[
              { label: "온라인", value: rows.filter((m) => m.connected).length, icon: <Wifi />, tone: "teal", note: "현재 서버에 연결된 PC", onOpen: () => setFilter("online"), openLabel: "온라인 머신만 보기" },
              { label: "연결 끊김", value: rows.filter((m) => !m.connected).length, icon: <WifiOff />, tone: "coral", note: "현장 연결 확인 필요", onOpen: () => setFilter("disconnected"), openLabel: "연결 끊긴 머신만 보기" },
              { label: "업데이트 가능", value: rows.filter((m) => updatable.has(m.id)).length, icon: <ArrowUpCircle />, tone: "blue", note: "새 버전 설치 가능", onOpen: () => setFilter("update"), openLabel: "업데이트 가능한 머신만 보기" },
            ]}
          />
        </section>
      )}
      <div className="meta-crew-machine-filters">
        <div className="meta-crew-machine-search">
          <Field label="머신 검색" placeholder={showCustomer ? "머신 이름 또는 고객사" : "머신 이름"} value={query} onChange={(e) => setQuery(e.target.value)} />
          <Search size={15} aria-hidden="true" />
        </div>
        {showCustomer && (
          <Select label="고객사" value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
            <option value="">전체 고객사</option>
            {customers.map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </Select>
        )}
        <Select label="머신 상태" value={filter} onChange={(e) => setFilter(e.target.value as MachineFilter)}>
          <option value="all">전체 머신</option>
          <option value="online">온라인</option>
          <option value="disconnected">연결 끊김</option>
          <option value="update">업데이트 가능</option>
          {deletedUrl && <option value="deleted">삭제됨</option>}
        </Select>
      </div>
      <div className="meta-crew-machine-result-count" aria-live="polite">
        머신 <strong>{visible.length}대</strong>
        {filter === "deleted" && <span>삭제된 머신의 마지막 기록입니다. 머신 기록에서 그 머신이 한 작업을 볼 수 있습니다.</span>}
      </div>
      {filter === "deleted" && deletedQ.isLoading ? (
        <Loading />
      ) : visible.length ? (
        <div className="meta-crew-machine-grid">
          {visible.map((m) => {
            const deleted = !!m.deleted;
            const pending = m.pendingDeviceCount ?? 0;
            const llm = m.lastState?.llm as { available?: boolean } | undefined;
            return (
              <article key={m.id} className={`meta-crew-machine-card${deleted ? " is-deleted" : ""}`}>
                <header>
                  <div className="meta-crew-machine-card-identity">
                    <span className="meta-crew-machine-icon">
                      <Monitor size={19} />
                    </span>
                    <div>
                      <h3>{m.label}</h3>
                      {showCustomer && <small>{m.customerName ?? "고객사 확인 필요"}</small>}
                    </div>
                  </div>
                  <StatusBadge label={machineStatus(m)} />
                </header>
                <div className="meta-crew-machine-meta">
                  <span>{m.version ? `v${m.version}` : "버전 정보 없음"}</span>
                  {updatable.has(m.id) && <Badge tone="blue">업데이트 가능 · {updatable.get(m.id)}</Badge>}
                  <span>{m.pairedAt ? `마지막 수신 ${ago(m.lastSeenAt)}` : "현장 페어링 대기"}</span>
                </div>
                <MachineDeviceSummary
                  deviceCount={m.deviceCount ?? 0}
                  pendingDeviceCount={pending}
                  extra={
                    staff
                      ? [
                          { label: "진행 중", value: m.runningTasks ?? 0, icon: <Activity />, tone: "blue" },
                          { label: "AI", value: !m.pairedAt ? "—" : llm?.available ? "준비됨" : "로그인 필요", icon: <Zap />, tone: llm?.available ? "teal" : "neutral" },
                        ]
                      : []
                  }
                />
                <footer>
                  {deleted ? (
                    <span>삭제일 {fmtDateTime(m.deletedAt ?? null)} · 연결이 해제되었습니다.</span>
                  ) : (
                    <span className={pending ? "has-pending" : ""}>{pending ? "폰에서 USB 디버깅을 허용해 주세요." : "디바이스는 머신 상세에서 확인하세요."}</span>
                  )}
                  <Button variant="secondary" size="sm" onClick={() => onOpen(m)}>
                    {deleted ? "머신 기록" : "머신 상세"} <ArrowUpRight size={14} />
                  </Button>
                </footer>
              </article>
            );
          })}
        </div>
      ) : (
        <div className="meta-crew-machine-empty">
          <Monitor size={28} />
          <strong>{rows.length || deletedRows.length ? "조건에 맞는 머신이 없습니다" : "등록된 머신이 없습니다"}</strong>
          <p>머신 이름이나 상태 필터를 바꿔 확인해 주세요.</p>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setFilter("all");
              setQuery("");
              setCustomerId("");
            }}
          >
            필터 초기화
          </Button>
        </div>
      )}
    </div>
  );
}

/** 머신 등록 → 바로 페어링 코드 발급 (기획 "페어링 코드는 누가 어디서 발급하나" 2단계). */
export function RegisterMachineDialog({ customerId, open, onClose }: { customerId: string; open: boolean; onClose: () => void }) {
  const formId = useId();
  const [label, setLabel] = useState("");
  const [issued, setIssued] = useState<{ machineId: string; label: string; pairing: Pairing } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const create = useAction((l: string) => api.post<{ machine: MachineRow; pairing: Pairing }>(`/c/${customerId}/machines`, { label: l }), [keys.machines(customerId)]);
  const reissue = useAction(() => api.post<Pairing>(`/c/${customerId}/machines/${issued!.machineId}/pairing-code`), []);
  useEffect(() => {
    if (open) {
      setLabel("");
      setIssued(null);
      setError(null);
    }
  }, [open]);
  return (
    <Dialog
      open={open}
      onOpenChange={(v) => !v && onClose()}
      title="머신 등록"
      description="머신을 등록하면 현장 PC에 입력할 8자리 코드가 바로 발급됩니다."
      footer={
        <DialogActions
          secondary={
            issued
              ? {
                  label: "코드 재발급",
                  onClick: () =>
                    reissue
                      .mutateAsync()
                      .then((p) => setIssued((x) => (x ? { ...x, pairing: p } : x)))
                      .catch((e) => setError(errorMessage(e))),
                }
              : { label: "취소", onClick: onClose }
          }
          primary={issued ? { label: "완료", onClick: onClose } : { label: "등록하고 코드 발급", type: "submit", form: formId, disabled: create.isPending }}
          note={error ?? undefined}
        />
      }
    >
      {issued ? (
        <PairingCode label={issued.label} pairing={issued.pairing} />
      ) : (
        <form
          id={formId}
          className="mcc-form"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            create
              .mutateAsync(label.trim())
              .then((r) => setIssued({ machineId: r.machine.id, label: r.machine.label, pairing: r.pairing }))
              .catch((er) => setError(errorMessage(er)));
          }}
        >
          <Field label="머신 이름" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="예: 강남점 PC" required maxLength={100} />
        </form>
      )}
    </Dialog>
  );
}

export function PairingCode({ label, pairing }: { label: string; pairing: Pairing }) {
  const left = Math.max(0, Math.round((new Date(pairing.expiresAt).getTime() - Date.now()) / 60000));
  const guide = `[Meta Crew 브릿지 연결 안내]\n1) 설치 파일: ${installerUrl}\n2) 프로그램 실행 후 연결 코드 입력: ${pairing.code}\n   (코드는 ${fmtTime(pairing.expiresAt)}까지 1회만 사용할 수 있습니다)`;
  return (
    <div className="mcc-pair-code">
      <Badge tone="blue">{label} · 미연결</Badge>
      <h2>{pairing.code}</h2>
      <p>
        {fmtTime(pairing.expiresAt)}까지 1회 사용 · 약 {left}분 남음
      </p>
      <p>설치 파일과 연결 코드를 현장 담당자에게 전달하세요.</p>
      <code>{pairing.link}</code>
      <div className="mcc-copy">
        <CopyButton text={pairing.code} label="코드 복사" />
        <CopyButton text={guide} label="안내문 복사" />
      </div>
    </div>
  );
}

/** 머신 lastState.deviceStates → serial 별 배터리·화면·키보드 (브릿지가 하트비트로 보고). */
export function deviceExtras(machines: Pick<MachineRow, "lastState">[]): Map<string, Record<string, unknown>> {
  const out = new Map<string, Record<string, unknown>>();
  for (const m of machines) {
    const st = (m.lastState ?? {}) as { deviceStates?: Record<string, unknown>[] };
    for (const d of Array.isArray(st.deviceStates) ? st.deviceStates : []) if (typeof d.serial === "string") out.set(d.serial, d);
  }
  return out;
}

type DeviceRowEx = Device & { customerName?: string; customerId?: string };

/**
 * 디바이스 목록 (시안 devices branch): 디바이스 · 현재 연결 · 상태 · 배터리 · 한글 키보드 · 마지막 확인.
 * editCustomerId 가 있고 DEVICE_WRITE 권한이면 별칭을 표 안에서 바로 고친다 (PATCH /c/:id/devices/:deviceId).
 * 디바이스별 연결 이력은 서버 API 가 없어 머신 상세의 연결 이력으로 대신한다.
 */
export function DevicesTable({
  rows,
  showMachine = true,
  extras,
  editCustomerId,
  showCustomer,
}: {
  rows: DeviceRowEx[];
  showMachine?: boolean;
  extras?: Map<string, Record<string, unknown>>;
  editCustomerId?: string | ((d: DeviceRowEx) => string);
  showCustomer?: boolean;
}) {
  const me = useAuth((s) => s.me);
  const canEdit = !!editCustomerId && can(me, "DEVICE_WRITE");
  const [editing, setEditing] = useState<string | null>(null);
  const [alias, setAlias] = useState("");
  const [blogId, setBlogId] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const cid = (d: DeviceRowEx) => (typeof editCustomerId === "function" ? editCustomerId(d) : (editCustomerId ?? ""));
  const save = useAction(
    (v: { customerId: string; id: string; alias: string | null; naverBlogId: string }) => api.patch(`/c/${v.customerId}/devices/${v.id}`, { alias: v.alias, naverBlogId: v.naverBlogId }),
    [["c"], ["admin"], ["seo"]],
  );
  if (rows.length === 0) return <Empty>연결된 디바이스가 없습니다. 브릿지 PC에 폰을 USB로 연결하면 자동으로 나타납니다.</Empty>;
  return (
    <>
      {err && <InfoRow title={err} />}
      <Table>
        <thead>
          <tr>
            <th>디바이스</th>
            {showCustomer && <th>고객사</th>}
            {showMachine && <th>현재 연결</th>}
            <th>상태</th>
            <th>배터리</th>
            <th>한글 키보드</th>
            <th>마지막 확인</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((d) => {
            const x = extras?.get(d.serial) ?? {};
            const battery = typeof x.batteryPct === "number" ? `${x.batteryPct}%${x.charging ? " · 충전 중" : ""}` : "확인 대기";
            const approved = d.state === "device";
            const keyboard = !approved ? "승인 후 확인" : x.keyboardReady === undefined ? "확인 대기" : x.keyboardReady ? "준비됨" : "작업 시작 시 자동 설치";
            return (
              <tr key={d.id}>
                <td>
                  {editing === d.id ? (
                    <form
                      className="lwd-actions"
                      onSubmit={(e) => {
                        e.preventDefault();
                        setErr(null);
                        save
                          .mutateAsync({ customerId: cid(d), id: d.id, alias: alias.trim() || null, naverBlogId: blogId.trim() })
                          .then(() => setEditing(null))
                          .catch((er) => setErr(errorMessage(er)));
                      }}
                    >
                      <Field label="별칭" value={alias} onChange={(e) => setAlias(e.target.value)} maxLength={100} autoFocus />
                      <Field label="네이버 블로그 ID" placeholder="blog.naver.com/아이디" value={blogId} onChange={(e) => setBlogId(e.target.value)} maxLength={100} />
                      <Button size="sm" type="submit" disabled={save.isPending}>
                        저장
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>
                        취소
                      </Button>
                    </form>
                  ) : (
                    <>
                      {d.alias ?? d.model ?? d.serial}
                      {canEdit && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setAlias(d.alias ?? "");
                            setBlogId(d.naverBlogId ?? "");
                            setEditing(d.id);
                          }}
                        >
                          편집
                        </Button>
                      )}
                    </>
                  )}
                  <small className="meta-crew-sub">
                    {d.model ?? "모델 정보 없음"} · {d.serial}
                    {d.naverBlogId ? ` · 블로그 ${d.naverBlogId}` : " · 블로그 ID 미입력"}
                  </small>
                </td>
                {showCustomer && <td>{d.customerName ?? "—"}</td>}
                {showMachine && <td>{d.machineLabel ?? "연결 안 됨"}</td>}
                <td>
                  <StatusBadge label={d.enabled ? (deviceState[d.state] ?? [d.state, "neutral"]) : ["사용 중지", "neutral"]} />
                  {d.runningTaskId && <small className="meta-crew-sub">작업 실행 중</small>}
                </td>
                <td>{battery}</td>
                <td>{keyboard}</td>
                <td>{ago(d.lastSeenAt)}</td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </>
  );
}


/**
 * 머신 상세 (시안 MachineView). 직원·고객 화면과 Electron(머신 계정)이 같은 화면을 쓴다.
 * local=true 면 이 PC 에서 연 것이라 네이티브 조치(AI 로그인, 폰 준비, 로그 폴더)를 보여준다.
 */
export function MachineView({
  customerId,
  machineId,
  customerStatus,
  local = false,
  onOpenTask,
  onDeleted,
}: {
  customerId: string;
  machineId: string;
  customerStatus: CustomerStatus;
  local?: boolean;
  onOpenTask: (runId: string | null) => void;
  /** 머신을 지운 뒤 (상세 창을 닫는다). */
  onDeleted?: () => void;
}) {
  const me = useAuth((s) => s.me);
  const q = useMachine(customerId, machineId);
  const tasks = useTasks(customerId, { machineId, limit: 30 });
  const [tab, setTab] = useState("상태");
  const [pairing, setPairing] = useState<Pairing | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const localStatus = useLocalStatus(local);
  const inv = [keys.machine(customerId, machineId), keys.machines(customerId)];
  const reissue = useAction(() => api.post<Pairing>(`/c/${customerId}/machines/${machineId}/pairing-code`), inv);
  const unpair = useAction(() => api.post(`/c/${customerId}/machines/${machineId}/unpair`), inv);
  // 해제·폐기·은퇴된 머신 지우기 (직원만): 보관 삭제. 목록에서 빠지고 "삭제됨" 필터와 작업 이력에 남는다
  const remove = useAction(() => api.del(`/c/${customerId}/machines/${machineId}`), [keys.machines(customerId), keys.adminMachines, ["machines-deleted"]]);
  const command = useAction((a: "pause" | "resume") => api.post(`/c/${customerId}/machines/${machineId}/${a}`), inv);
  // AI 실행 방식 (docs/meta-crew-llm-relay-design.md): 직원만. null = 고객사 요금제 기본
  const aiModels = useAiModels(!local && !!me?.isStaff);
  const setLlmMode = useAction((v: "local" | "relay" | null) => api.patch(`/c/${customerId}/machines/${machineId}`, { llmMode: v }), inv);
  const latestFor = useUpdatable(customerId, !local).get(machineId);

  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={errorMessage(q.error)} onRetry={() => void q.refetch()} />;
  const m = q.data as MachineDetail;
  if (m.deleted) return <DeletedMachine m={m} tasks={tasks.data?.items ?? []} onOpenTask={onOpenTask} />;
  const st = m.lastState ?? {};
  const offline = m.pairedAt ? !m.connected : false;
  const socketDown = local && localStatus?.socket !== undefined && localStatus.socket !== "connected";
  const devicesLive = m.devices.filter((d) => d.machineId === m.id || !d.machineId);
  const approved = devicesLive.filter((d) => d.state === "device").length;
  const pending = devicesLive.filter((d) => d.state !== "device").length;
  const aiReady = !!st.llm?.available;
  const throttled = st.llm?.throttledUntil;
  const operable = customerStatus === "active" || customerStatus === "overdue";
  const canPair = can(me, "MACHINE_PAIR");
  const canCommand = can(me, "MACHINE_COMMAND");
  const running = (tasks.data?.items ?? []).filter((t) => ["assigned", "running"].includes(t.status));
  const recent = (tasks.data?.items ?? []).filter((t) => !["assigned", "running", "queued"].includes(t.status));
  const failedFirst = [...recent].sort((a, b) => Number(b.status === "failed") - Number(a.status === "failed"));
  const today = recent.filter((t) => t.finishedAt && new Date(t.finishedAt).toDateString() === new Date().toDateString());
  const run = (fn: () => Promise<unknown>, done: string) => {
    setNotice(null);
    fn()
      .then(() => setNotice(done))
      .catch((e) => setNotice(errorMessage(e)));
  };
  const nativeCall = (fn: () => Promise<{ ok: boolean; message?: string }>, done: string) =>
    run(async () => {
      const r = await fn();
      if (!r.ok) throw new Error(r.message ?? "실패했습니다.");
      if (r.message) setNotice(r.message);
    }, done);

  const notPaired = !m.pairedAt || m.status === "unpaired" || m.status === "revoked";
  const readiness = !notPaired && (!aiReady || approved === 0);
  // 해제·폐기·은퇴된 머신 지우기 (직원만): 머신 정보는 모두 지우고 작업 기록은 남긴다
  // 2026-10-21: 삭제 줄은 직원에게 늘 보이고, 연결된 머신이면 잠긴 채 "먼저 페어링 해제" 를 안내한다 (버튼을 못 찾는 문제).
  // 한 번도 접속하지 않은 머신(옛 앱으로 같은 PC 를 다시 페어링해 남은 것)은 해제 없이 지울 수 있다 (서버와 같은 규칙).
  const deletable = ["unpaired", "revoked", "retired"].includes(m.status) || (!m.lastSeenAt && !m.connected);
  const deleteRow = !local && me?.isStaff && canPair && (
    <InfoRow
      title="머신 삭제"
      description={
        deletable
          ? "연결이 해제된 머신을 목록에서 지웁니다. 토큰·페어링 코드·상태 이력·폰 연결 기록은 모두 지워지고, 작업 기록은 남습니다."
          : "연결된 머신은 지울 수 없습니다. 위의 \"페어링 해제\"를 먼저 하면 삭제할 수 있습니다."
      }
    >
      <Button
        size="sm"
        variant="danger"
        disabled={!deletable}
        onClick={() => {
          if (!window.confirm(`${m.label} 머신을 삭제할까요? 되돌릴 수 없습니다.`)) return;
          remove
            .mutateAsync()
            .then(() => onDeleted?.())
            .catch((e) => setNotice(errorMessage(e)));
        }}
      >
        머신 삭제
      </Button>
    </InfoRow>
  );

  return (
    <>
      <div className="mcc-machine-heading">
        <div className="mcc-avatar">
          <Monitor size={24} />
        </div>
        <div>
          <h2>{m.label}</h2>
          <p>
            {m.version ? `v${m.version}` : "버전 정보 없음"} · 마지막 수신 {ago(m.lastSeenAt)} · 동시 실행 {m.maxConcurrent ?? "-"}개
          </p>
        </div>
        <StatusBadge label={machineStatus(m)} />
      </div>
      {(offline || socketDown) && (
        <div className="mcc-banner">
          <Clock3 size={18} />
          <div>
            <strong>{local ? "서버에 다시 연결하고 있습니다" : "이 머신이 서버와 연결되어 있지 않습니다"}</strong>
            <p>{local ? "마지막 수신 정보를 표시합니다. 연결이 복구되면 자동으로 갱신됩니다." : `마지막 수신 ${fmtDateTime(m.lastSeenAt)} · 현장 PC의 전원과 네트워크를 확인하세요.`}</p>
          </div>
        </div>
      )}
      {notice && (
        <div className="mcc-banner">
          <div>
            <strong>{notice}</strong>
          </div>
        </div>
      )}
      {notPaired ? (
        <Panel title="현장 페어링 대기">
          <InfoRow
            title="아직 PC가 연결되지 않았습니다"
            description={canPair ? "설치 파일을 실행한 뒤 8자리 코드를 입력하면 연결됩니다." : "담당자가 현장 PC 연결을 진행합니다. 연결되면 상태가 자동으로 바뀝니다."}
          >
            {canPair && (
              <Button size="sm" variant="secondary" onClick={() => reissue.mutateAsync().then(setPairing).catch((e) => setNotice(errorMessage(e)))}>
                코드 발급
              </Button>
            )}
          </InfoRow>
          {deleteRow}
          {pairing && <PairingCode label={m.label} pairing={pairing} />}
        </Panel>
      ) : (
        <>
          {readiness && (
            <div className="mcc-banner">
              <div>
                <strong>연결 확인이 필요합니다</strong>
                <p>AI 로그인과 디바이스 연결이 모두 되어야 작업을 받을 수 있습니다.</p>
              </div>
            </div>
          )}
          <Tabs items={["상태", "작업", "디바이스", ...(local || canCommand || canPair ? ["설정"] : []), ...(!local && can(me, "SYSTEM_READ") ? ["버전"] : [])]} value={tab} onChange={setTab} />
          {/* 끊긴 머신은 흐리게 막되, 설정 탭은 열어 둔다 (꺼진 PC 해제·미접속 머신 삭제는 끊긴 상태에서 해야 한다) */}
          <div className={offline && tab !== "설정" ? "mcc-stale" : ""}>
            {tab === "상태" && (
              <>
                <div className="meta-crew-status-strip">
                  {[
                    [Monitor, "서버", local ? (localStatus?.socket === "connected" ? "연결됨" : "다시 연결 중") : m.connected ? "연결됨" : "끊김", offline || socketDown],
                    [Activity, "프로그램", m.version ? `v${m.version}${latestFor ? ` · ${latestFor} 업데이트 가능` : " · 최신"}` : "-", !!latestFor],
                    [Zap, "AI", throttled ? `한도 도달 · ${fmtTime(throttled)} 복구 예상` : aiReady ? "로그인됨" : "로그인 필요", !aiReady || !!throttled],
                    [Smartphone, "디바이스", devicesLive.length ? `${approved}대 준비${pending ? ` · ${pending}대 확인` : ""}` : "연결 필요", approved === 0 || pending > 0],
                  ].map(([Icon, title, detail, warn]) => {
                    const I = Icon as typeof Monitor;
                    return (
                      <div key={String(title)}>
                        <I size={16} />
                        <span>
                          <strong>{String(title)}</strong>
                          <small>{String(detail)}</small>
                        </span>
                        <i className={warn ? "warn" : ""} />
                      </div>
                    );
                  })}
                </div>
                <StatGrid
                  links={{
                    // 누르면 이 머신의 작업·디바이스·설정 탭 (2026-10-21)
                    "진행 중": { onOpen: () => setTab("작업"), label: "작업 탭에서 보기" },
                    "오늘 성공": { onOpen: () => setTab("작업"), label: "작업 탭에서 보기" },
                    "오늘 실패": { onOpen: () => setTab("작업"), label: "작업 탭에서 실패 먼저 보기" },
                    "확인 필요": pending ? { onOpen: () => setTab("디바이스"), label: "디바이스 탭에서 승인 대기 보기" } : !aiReady && (local || canCommand || canPair) ? { onOpen: () => setTab("설정"), label: "설정 탭에서 AI 상태 보기" } : undefined,
                  }}
                  items={[
                    ["진행 중", running.length, "이 PC의 작업"],
                    ["오늘 성공", today.filter((t) => t.status === "succeeded").length, "중앙 집계", "teal"],
                    ["오늘 실패", today.filter((t) => t.status === "failed").length, "실패 먼저 확인", "coral"],
                    ["확인 필요", pending + (aiReady ? 0 : 1), pending ? "USB 승인 대기" : aiReady ? "없음" : "AI 로그인", "amber"],
                  ]}
                />
                {/* 시안 c9ee685: 머신 상태에 지금 실행 중인 작업 → 실행 상세(실시간 로그 탭) */}
                {!local && <ActiveRunCards cards={running.map((t) => cardFromTask(t, customerId))} onOpen={(c) => onOpenTask(c.runId)} />}
                {me && <MachineLiveState me={me} customerId={customerId} machineId={machineId} />}
                <Panel title="디바이스별 작업" extra={<Badge>{devicesLive.length}대 연결</Badge>}>
                  {devicesLive.length === 0 && <InfoRow title="연결된 폰이 없습니다" description="USB 디버깅을 켜고 연결 시 '항상 허용'을 선택하세요." />}
                  {devicesLive.map((d) => {
                    const t = running.find((x) => x.assignedDeviceSerial === d.serial);
                    return (
                      <InfoRow
                        key={d.serial}
                        title={m.devices.find((x) => x.serial === d.serial)?.alias ?? d.model ?? d.serial}
                        description={
                          d.state !== "device"
                            ? "USB 승인 대기 · 폰에서 '항상 허용'을 선택하세요"
                            : !operable
                              ? "서비스 상태 때문에 새 작업을 받지 않습니다"
                              : t
                                ? `${t.title ?? taskTypeNames[t.type]} · 시작 ${fmtTime(t.startedAt)}`
                                : "새 작업을 기다리고 있습니다"
                        }
                      >
                        <Badge tone={d.state !== "device" ? "amber" : t ? "blue" : "neutral"}>{d.state !== "device" ? "현장 조치" : t ? "실행 중" : "대기"}</Badge>
                        {t && (
                          <Button size="sm" variant="secondary" onClick={() => onOpenTask(t.runId)}>
                            작업 상세
                          </Button>
                        )}
                        {local && d.state === "device" && (
                          <Button size="sm" variant="ghost" onClick={() => nativeCall(() => native()!.setupPhone(d.serial), "폰 준비를 마쳤습니다.")}>
                            폰 준비
                          </Button>
                        )}
                      </InfoRow>
                    );
                  })}
                </Panel>
              </>
            )}
            {tab === "작업" && (
              <>
                <Panel title="진행 중" extra={<Badge>읽기 전용</Badge>}>
                  {running.length === 0 && <InfoRow title="진행 중인 작업이 없습니다" description="대기 순서와 배정은 중앙에서 정합니다." />}
                  {running.map((t) => (
                    <InfoRow key={t.id} title={t.title ?? taskTypeNames[t.type] ?? t.type} description={`${t.assignedDeviceSerial ?? "폰 배정 중"} · 시작 ${fmtTime(t.startedAt)}`}>
                      <StatusBadge label={taskLabel(t.status)} />
                      <Button size="sm" variant="secondary" onClick={() => onOpenTask(t.runId)}>
                        상세
                      </Button>
                    </InfoRow>
                  ))}
                </Panel>
                <Panel title="최근 작업 · 실패 먼저">
                  {failedFirst.length === 0 && <InfoRow title="끝난 작업이 없습니다" />}
                  {failedFirst.slice(0, 20).map((t) => (
                    <InfoRow key={t.id} title={t.title ?? taskTypeNames[t.type] ?? t.type} description={`${fmtTime(t.finishedAt)} · ${(t.summary ?? t.error ?? "").slice(0, 80)}`}>
                      <StatusBadge label={taskLabel(t.status)} />
                      <Button size="sm" variant="ghost" onClick={() => onOpenTask(t.runId)}>
                        상세
                      </Button>
                    </InfoRow>
                  ))}
                </Panel>
              </>
            )}
            {tab === "디바이스" && (
              <Panel title="연결된 디바이스">
                <DevicesTable rows={m.devices} showMachine={false} extras={deviceExtras([m])} editCustomerId={customerId} />
              </Panel>
            )}
            {tab === "버전" && <MachineVersionPanel customerId={customerId} machineId={machineId} canUpdate={canCommand} />}
            {tab === "설정" && (
              <>
                <Panel title="AI와 디바이스 연결">
                  <InfoRow title={aiReady ? "AI 로그인됨" : "AI 로그인이 필요합니다"} description={throttled ? `사용 한도 도달 · ${fmtDateTime(throttled)} 복구 예상` : st.llm?.version ? `Codex ${st.llm.version}` : ""}>
                    {local ? (
                      <>
                        <Button size="sm" variant="secondary" onClick={() => nativeCall(() => native()!.codexLogin(), "브라우저에서 로그인을 마치면 자동으로 반영됩니다.")}>
                          {aiReady ? "다시 로그인" : "AI 로그인"}
                        </Button>
                        {aiReady && (
                          <Button size="sm" variant="ghost" onClick={() => nativeCall(() => native()!.codexLogout(), "로그아웃했습니다.")}>
                            로그아웃
                          </Button>
                        )}
                      </>
                    ) : (
                      <Badge>로그인 변경은 현장에서</Badge>
                    )}
                  </InfoRow>
                  <InfoRow title={`디바이스 ${approved}대 준비${pending ? ` · ${pending}대 승인 대기` : ""}`} description="USB 디버깅 '항상 허용'과 한글 키보드를 확인하세요.">
                    {local && (
                      <Button size="sm" variant="secondary" onClick={() => nativeCall(() => native()!.setupPhone(), "폰 준비를 마쳤습니다.")}>
                        폰 준비
                      </Button>
                    )}
                  </InfoRow>
                </Panel>
                <Panel title={local ? "이 PC의 프로그램" : "원격 관리"}>
                  <InfoRow title={`버전 ${m.version ?? "-"}${latestFor ? ` → 최신 ${latestFor}` : ""}`} description={`동시 실행 상한 ${m.maxConcurrent ?? "-"}개 · 중앙에서 설정`}>
                    {!local && latestFor && (
                      <Button size="sm" onClick={() => setTab("버전")}>
                        업데이트
                      </Button>
                    )}
                    {local && (
                      <Button size="sm" variant="secondary" onClick={() => nativeCall(() => native()!.checkUpdate(), "최신 버전입니다.")}>
                        업데이트 확인
                      </Button>
                    )}
                  </InfoRow>
                  {st.system && (
                    <InfoRow
                      title={`CPU ${num(st.system.cpuPct)}% · 메모리 ${num(st.system.memPct)}% · 디스크 여유 ${num(st.system.diskFreePct)}%`}
                      description={`가동 ${Math.round((st.system.uptimeSec ?? 0) / 3600)}시간 · 마지막 하트비트 ${fmtTime(st.at ?? m.lastSeenAt)}`}
                    />
                  )}
                  {!local && me?.isStaff && (
                    <MachineAiMode
                      key={m.id}
                      canEdit={can(me, "MACHINE_COMMAND")}
                      view={{
                        mode: (m as { llmMode?: "local" | "relay" | null }).llmMode ?? null,
                        defaultMode: (m as { llmDefaultMode?: "local" | "relay" }).llmDefaultMode ?? "local",
                        loggedIn: !!((st.llm as { loggedIn?: boolean } | undefined)?.loggedIn ?? st.llm?.available),
                        bridgeReady: ((st as { capabilities?: string[] }).capabilities ?? []).includes("llm.relay"),
                        readiness: relayReadiness(aiModels.data?.relay),
                      }}
                      onSave={async (mode) => {
                        try {
                          await setLlmMode.mutateAsync(mode);
                        } catch (e) {
                          throw new Error(errorMessage(e));
                        }
                      }}
                    />
                  )}
                  {!local && canCommand && operable && (
                    <InfoRow title={m.status === "paused" ? "작업 배정 중지됨" : "작업 배정 중"} description="중지하면 이 PC에 새 작업을 배정하지 않습니다.">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => run(() => command.mutateAsync(m.status === "paused" ? "resume" : "pause"), m.status === "paused" ? "배정을 재개했습니다." : "배정을 중지했습니다.")}
                      >
                        {m.status === "paused" ? "배정 재개" : "배정 중지"}
                      </Button>
                    </InfoRow>
                  )}
                  {!local && canPair && (
                    <InfoRow title="페어링" description={`연결 ${fmtDateTime(m.pairedAt)} · PC 교체 시 해제 후 새 코드로 연결하면 이력이 이어집니다.`}>
                      <Button size="sm" variant="secondary" onClick={() => reissue.mutateAsync().then(setPairing).catch((e) => setNotice(errorMessage(e)))}>
                        코드 재발급
                      </Button>
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() => {
                          if (window.confirm(`${m.label}의 페어링을 해제할까요? 이 PC는 즉시 연결이 끊깁니다.`)) run(() => unpair.mutateAsync(), "페어링을 해제했습니다.");
                        }}
                      >
                        페어링 해제
                      </Button>
                    </InfoRow>
                  )}
                  {deleteRow}
                  {pairing && <PairingCode label={m.label} pairing={pairing} />}
                  {local && (
                    <details className="mcc-details">
                      <summary>개발자 정보</summary>
                      <p>
                        네이티브 API v{native()?.version} · 소켓 {localStatus?.socket ?? "-"} · 마지막 수신 {fmtTime(localStatus?.lastReceivedAt)}
                        {localStatus?.engineError ? ` · 오류: ${localStatus.engineError}` : ""}
                      </p>
                      <div className="meta-crew-buttons">
                        <Button size="sm" variant="secondary" onClick={() => nativeCall(() => native()!.openLogs(), "로그 폴더를 열었습니다.")}>
                          로그 폴더
                        </Button>
                        <Button size="sm" variant="secondary" onClick={() => nativeCall(() => native()!.restart(), "프로그램을 다시 시작합니다.")}>
                          프로그램 재시작
                        </Button>
                      </div>
                    </details>
                  )}
                </Panel>
              </>
            )}
          </div>
        </>
      )}
    </>
  );
}

/** Electron 안에서만: 엔진 소켓 상태 (서버가 끊겼을 때도 보여줄 수 있는 유일한 로컬 정보). */
export function useLocalStatus(enabled: boolean): LocalStatus | null {
  const [s, setS] = useState<LocalStatus | null>(null);
  useEffect(() => {
    const n = native();
    if (!enabled || !n) return;
    void n.getLocalStatus().then(setS);
    return n.onLocalStatus(setS);
  }, [enabled]);
  return s;
}

export function useCustomerDevices(customerId: string) {
  return useDevices(customerId);
}

/** 삭제(보관)한 머신 상세 — 읽기 전용 (2026-10-21 사용자: 삭제한 머신과 그 머신에서 한 작업 이력을 관리자가 볼 수 있어야 한다). */
function DeletedMachine({ m, tasks, onOpenTask }: { m: MachineDetail; tasks: TaskRow[]; onOpenTask: (runId: string | null) => void }) {
  return (
    <>
      <div className="mcc-machine-heading">
        <div className="mcc-avatar">
          <Monitor size={24} />
        </div>
        <div>
          <h2>{m.label}</h2>
          <p>
            ID {m.id.slice(0, 8)} · {m.version ? `v${m.version}` : "버전 정보 없음"} · 페어링 {fmtDateTime(m.pairedAt)}
          </p>
        </div>
        <StatusBadge label={machineStatus(m)} />
      </div>
      <div className="mcc-banner">
        <Clock3 size={18} />
        <div>
          <strong>삭제된 머신입니다 · {fmtDateTime(m.deletedAt ?? null)}</strong>
          <p>다시 연결하거나 바꿀 수 없습니다. 이 머신에서 한 작업 기록은 그대로 남아 있습니다.</p>
        </div>
      </div>
      <Panel title="이 머신에서 한 작업" extra={<Badge>읽기 전용</Badge>}>
        {tasks.length === 0 && <InfoRow title="이 머신에서 한 작업이 없습니다" />}
        {tasks.map((t) => (
          <InfoRow key={t.id} title={t.title ?? taskTypeNames[t.type] ?? t.type} description={`${fmtDateTime(t.startedAt ?? t.createdAt)} · ${t.assignedDeviceSerial ?? "-"} · ${(t.summary ?? t.error ?? "").slice(0, 80)}`}>
            <StatusBadge label={taskLabel(t.status)} />
            <Button size="sm" variant="ghost" onClick={() => onOpenTask(t.runId)}>
              상세
            </Button>
          </InfoRow>
        ))}
      </Panel>
    </>
  );
}
